import styled, { css } from 'styled-components';

import { ACQ, ANIM, ACQ_NARROW, ACQ_MOBILE } from '../../../../theme/tokens';
import { hoverWash } from '../../../../theme/mixins';
import { Button, Text } from '../../../../components';

export const Card = styled.div`
  background: ${ACQ.cardOuter};
  border-radius: 16px;
  padding: 10px;
`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 304px;
  gap: 10px;

  ${ACQ_NARROW} {
    grid-template-columns: 1fr;
  }
`;

export const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

export const SecTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 700;
  color: ${ACQ.t300};
  padding: 10px 0 10px 5px;
`;

export const SecSpark = styled.img`
  width: 16px;
  height: 16px;
`;

export const ChannelsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 1fr;
  gap: 10px;
  flex: 1;
  min-height: 0;
`;

export const Channel = styled.div<{ $gradient: string; $border: string; $locked?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${ACQ.cardInner};
  background-image: ${(p) => p.$gradient};
  background-size: 100% 100%;
  border: 1px solid ${(p) => p.$border};
  border-radius: 16px;
  padding: 10px;
  /* Positioning context for the LockVeil (a locked card drops hoverWash, which would
     otherwise establish it). */
  position: relative;
  cursor: ${(p) => (p.$locked ? 'default' : 'pointer')};
  /* Locked: clip the frosted LockVeil to the rounded corners. Only when locked, so a
     live card's focus outline (which sits outside the box) is never clipped. */
  overflow: ${(p) => (p.$locked ? 'hidden' : 'visible')};
  /* ...and isolate the card's stacking so the veil (z-indexed above the card's own
     content) stays CONTAINED in the card. Without this the veil escapes to the page
     layer and paints over the header scrim, so a locked card near the top bleeds up
     into the header band instead of fading under it like every other tucked card. */
  ${(p) => (p.$locked ? 'isolation: isolate;' : '')}

  /* Hover/focus affordances only on a LIVE card — a locked card is inert (no wash, no
     chevron nudge, not focusable). */
  ${(p) =>
    p.$locked
      ? ''
      : css`
          ${hoverWash}

          &:hover .chev {
            transform: translateX(2px);
            color: ${ACQ.white};
          }
          &:focus-visible {
            outline: 2px solid ${ACQ.orange};
            outline-offset: 2px;
          }
        `}
`;

export const ChMain = styled.div`
  flex: 1;
`;

export const ChTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 5px;
`;

export const ChIcon = styled.span`
  color: ${ACQ.t300};
  display: flex;
`;

export const ChName = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  color: ${ACQ.t50};
`;

export const ChBar = styled.div`
  height: 8px;
  border-radius: 100px;
  background: ${ACQ.barTrack};
  margin: 20px 0 10px;
  overflow: hidden;
`;

export const ChBarFill = styled.span<{ $color: string; $pct: number; $mounted: boolean; $index: number }>`
  display: block;
  height: 100%;
  border-radius: 100px;
  background: ${(p) => p.$color};
  /* Default (and reduced-motion) state: render at the final width instantly,
     no transition — so there is never a flash of an empty bar. */
  width: ${(p) => Math.min(100, p.$pct)}%;

  /* Entrance only when motion is welcome: start at 0 before mount, then grow to
     the real width over a tasteful duration, staggered per card by $index. */
  @media (prefers-reduced-motion: no-preference) {
    width: ${(p) => (p.$mounted ? Math.min(100, p.$pct) : 0)}%;
    transition: width ${ANIM.ACQ_BAR_ENTER_MS}ms ${ACQ.ease};
    transition-delay: ${(p) => p.$index * ANIM.STAGGER_MS}ms;
  }
`;

export const ChFoot = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
`;

export const ChSpent = styled.span`
  display: inline-flex;
  font-size: 12px;
  font-weight: 300;
  color: ${ACQ.t300};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const Dim = styled(Text)`
  color: ${ACQ.spentDim};
`;

export const ChCpa = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  color: ${ACQ.t300};
  white-space: nowrap;
`;

export const ChBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  min-height: 24px;
`;

export const ChStatus = styled(Text)<{ $color: string }>`
  font-size: 12px;
  font-weight: 400;
  color: ${(p) => p.$color};
`;

export const Chev = styled.span.attrs({ className: 'chev' })`
  display: grid;
  place-items: center;
  color: ${ACQ.t50};
  flex-shrink: 0;
  transition:
    transform 0.25s ${ACQ.ease},
    color 0.2s;
`;

// --- Budgets column ---------------------------------------------------------
export const BudgetCard = styled.div`
  flex: 1;
  background: ${ACQ.cardInner};
  border-radius: 16px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const BudgetSubs = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: center;

  ${ACQ_MOBILE} {
    flex-direction: row;
  }
`;

export const BudgetSub = styled.div`
  flex: 1;
  min-height: 110px;
  background: ${ACQ.cardOuter};
  border-radius: 8px;
  padding: 10px 15px;
  display: flex;
  flex-direction: column;
`;

export const BudgetIcon = styled.span`
  color: ${ACQ.t300};
  display: flex;
  margin-bottom: 10px;
`;

export const BudgetVal = styled(Text)`
  font-size: 24px;
  font-weight: 700;
  color: ${ACQ.t50};
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  padding-top: 5px;
`;

export const BudgetLbl = styled(Text)`
  font-size: 14px;
  color: ${ACQ.t50};
  margin-top: 10px;
`;

// Sizing-only wrapper around the shared Button: matches the original CTA's
// 14px/700 type. Theming (background/border/colour) is owned by Button.
export const BudgetCta = styled(Button)`
  font-size: 14px;
  font-weight: 700;
`;

// --- Disclaimer footnote ----------------------------------------------------
// A quiet, full-width note under both columns: figures are yesterday's, and any
// limit/budget edits land the next day. A hairline rule lifts it off the cards,
// an info glyph anchors it, the copy stays muted so it never competes for focus.
export const Disclaimer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  /* Even rhythm around the rule: equal air above the hairline and below it to
     the copy (and the copy to the card edge, via the Card's 10px padding). */
  margin-top: 16px;
  padding: 16px 12px 6px;
  border-top: 1px solid ${ACQ.divider};
`;

export const DisclaimerIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  margin-top: 1px;
  color: ${ACQ.t400};
`;

export const DisclaimerText = styled(Text)`
  max-width: 660px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.01em;
  text-align: center;
  color: ${ACQ.t400};
  font-variant-numeric: tabular-nums;
`;
